import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Button, IconButton, Link, Stack, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { buscarProveedoresFactura, fmtMonto } from "../services/solicitudChequeService";

const hoy = () => new Date().toISOString().slice(0, 10);
export const facturaVacia = () => ({ nro_factura: "", fecha_factura: hoy(), nit: "", denominacion: "", nro_autorizacion: "", cod_control: "", importe_total: "", importe_no_sujeto: "", descuento: "" });

const numero = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
// Crédito fiscal (IVA 13%) sobre lo que sí está sujeto: importe total − no sujeto − descuento.
const creditoFiscal = (f) => Math.max(numero(f.importe_total) - numero(f.importe_no_sujeto) - numero(f.descuento), 0) * 0.13;

// Etiqueta arriba del campo: los campos quedan compactos y se leen igual dentro y fuera de la ficha de la solicitud.
const Campo = ({ etiqueta, requerido, span, children }) => (
  <Box sx={{ display: "grid", gap: 0.25, gridColumn: span, minWidth: 0 }}>
    <Typography component="label" sx={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary" }}>
      {etiqueta}{requerido && <Box component="span" sx={{ color: "error.main" }}> *</Box>}
    </Typography>
    {children}
  </Box>
);

const campoSx = { "& .MuiInputBase-root": { fontSize: 13 }, "& .MuiInputBase-input": { py: "6px" } };

// NIT con búsqueda de proveedores que ya tienen facturas registradas: al elegir uno se completa la razón social.
function CampoNit({ factura, onCambiar }) {
  const [opciones, setOpciones] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const texto = factura.nit;

  useEffect(() => {
    if (String(texto).trim().length < 2) { setOpciones([]); return undefined; }
    let vivo = true;
    const t = setTimeout(() => {
      setBuscando(true);
      buscarProveedoresFactura(texto.trim())
        .then(({ data }) => { if (vivo) setOpciones(Array.isArray(data) ? data : []); })
        .catch(() => { if (vivo) setOpciones([]); })
        .finally(() => { if (vivo) setBuscando(false); });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [texto]);

  return (
    <Autocomplete
      freeSolo size="small" options={opciones} loading={buscando} filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : String(o.nit || "").trim())}
      isOptionEqualToValue={(o, v) => o.nit === v.nit && o.denominacion === v.denominacion}
      inputValue={texto}
      onInputChange={(_, valor, motivo) => { if (motivo !== "reset") onCambiar({ nit: valor }); }}
      onChange={(_, o) => { if (o && typeof o === "object") onCambiar({ nit: String(o.nit || "").trim(), denominacion: String(o.denominacion || "").trim() || factura.denominacion }); }}
      renderOption={({ key, ...props }, o) => (
        <Box component="li" key={key} {...props} sx={{ display: "grid !important", gap: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{o.nit}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{o.denominacion}</Typography>
        </Box>
      )}
      noOptionsText={String(texto).trim().length < 2 ? "Escribe al menos 2 dígitos" : "Sin proveedores con ese NIT; se guardará como nuevo"}
      renderInput={(params) => <TextField {...params} hiddenLabel placeholder="Buscar NIT" sx={campoSx} slotProps={{ htmlInput: { ...params.inputProps, inputMode: "numeric", "aria-label": "NIT del proveedor" } }} />}
    />
  );
}

// Datos de una o varias facturas de la solicitud (se guardan en la tabla de impuestos). El orden sigue el de la factura impresa.
export default function FacturasEditor({ facturas, onChange, permitirVacio = false }) {
  const [conControl, setConControl] = useState({}); // facturas antiguas (computarizadas) que muestran el código de control
  const cambiarVarios = (i, cambios) => onChange(facturas.map((x, j) => (j === i ? { ...x, ...cambios } : x)));
  const cambiar = (i, campo) => (e) => cambiarVarios(i, { [campo]: e.target.value });
  const total = facturas.reduce((s, f) => s + numero(f.importe_total), 0);
  const credito = facturas.reduce((s, f) => s + creditoFiscal(f), 0);

  const texto = (i, campo, etiqueta, extra = {}) => (
    <TextField
      fullWidth size="small" hiddenLabel value={facturas[i][campo]} onChange={cambiar(i, campo)} sx={campoSx}
      slotProps={{ htmlInput: { "aria-label": etiqueta, ...extra } }}
    />
  );

  return (
    <Stack spacing={1.25} sx={{ "& .MuiFormControl-root": { m: 0 } }}>
      {facturas.map((fa, i) => {
        const verControl = conControl[i] || !!String(fa.cod_control || "").trim();
        return (
          <Box key={i} sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75, minHeight: 28 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                Factura {i + 1}
                {fa.nro_factura.trim() && <Box component="span" sx={{ fontWeight: 500, color: "text.secondary" }}>{` · N.º ${fa.nro_factura.trim()}`}</Box>}
              </Typography>
              {(permitirVacio || facturas.length > 1) && (
                <Tooltip title="Quitar esta factura">
                  <IconButton size="small" aria-label={`Quitar factura ${i + 1}`} onClick={() => { setConControl({}); onChange(facturas.filter((_, j) => j !== i)); }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1 }}>
              <Campo etiqueta="NIT proveedor"><CampoNit factura={fa} onCambiar={(c) => cambiarVarios(i, c)} /></Campo>
              <Campo etiqueta="Razón social" span={{ xs: "span 1", sm: "span 3" }}>{texto(i, "denominacion", "Razón social del proveedor")}</Campo>

              <Campo etiqueta="N.º de factura" requerido>{texto(i, "nro_factura", "Número de factura", { inputMode: "numeric" })}</Campo>
              <Campo etiqueta="Fecha" requerido>
                <TextField fullWidth size="small" hiddenLabel type="date" value={fa.fecha_factura} onChange={cambiar(i, "fecha_factura")} sx={campoSx} slotProps={{ htmlInput: { "aria-label": "Fecha de la factura", max: hoy() } }} />
              </Campo>
              <Campo etiqueta="Cód. autorización / CUF" span={{ xs: "span 2", sm: "span 2" }}>{texto(i, "nro_autorizacion", "Código de autorización o CUF")}</Campo>

              <Campo etiqueta="Importe total (Bs)">{texto(i, "importe_total", "Importe total", { inputMode: "decimal" })}</Campo>
              <Campo etiqueta="No sujeto a CF">{texto(i, "importe_no_sujeto", "Importe no sujeto a crédito fiscal", { inputMode: "decimal" })}</Campo>
              <Campo etiqueta="Descuento">{texto(i, "descuento", "Descuento", { inputMode: "decimal" })}</Campo>
              <Campo etiqueta="Crédito fiscal 13%">
                <Box sx={{ height: 32, px: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", bgcolor: "action.hover", border: 1, borderColor: "divider", borderRadius: 1, fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {fmtMonto(creditoFiscal(fa))}
                </Box>
              </Campo>

              {verControl ? (
                <Campo etiqueta="Código de control (facturas antiguas)" span={{ xs: "span 2", sm: "span 4" }}>{texto(i, "cod_control", "Código de control")}</Campo>
              ) : (
                <Box sx={{ gridColumn: { xs: "span 2", sm: "span 4" } }}>
                  <Link component="button" type="button" underline="hover" onClick={() => setConControl((c) => ({ ...c, [i]: true }))} sx={{ fontSize: 12 }}>
                    ¿Factura antigua con código de control?
                  </Link>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: "none", fontWeight: 700 }} onClick={() => onChange([...facturas, facturaVacia()])}>
          {facturas.length ? "Agregar otra factura" : "Agregar factura"}
        </Button>
        {facturas.length > 1 && (
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
            {facturas.length} facturas · Total <b>Bs {fmtMonto(total)}</b> · Crédito fiscal <b>Bs {fmtMonto(credito)}</b>
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
